// Chat UI Component - Full MMO Chat System

import type { ChatChannel, ChatMessage } from 'shared';

export interface ChatTab {
  id: string;
  name: string;
  channels: ChatChannel[];
  unread: number;
}

export class ChatUI {
  private container: HTMLElement;
  private messagesContainer: HTMLElement | null = null;
  private inputElement: HTMLInputElement | null = null;
  private messages: ChatMessage[] = [];
  private tabs: ChatTab[] = [];
  private activeTabId: string = 'general';
  private isMinimized: boolean = false;
  private isInputFocused: boolean = false;
  private maxMessages: number = 200;
  private currentChannel: ChatChannel = 'say';
  private whisperTarget: string | null = null;

  // Channel colors
  private channelColors: Record<ChatChannel, string> = {
    say: '#ffffff',
    yell: '#ff4444',
    whisper: '#ff77ff',
    party: '#aaaaff',
    guild: '#44ff44',
    trade: '#ffaa00',
    world: '#ffff44',
    system: '#ffff00',
  };

  // Channel prefixes for input
  private channelPrefixes: Record<string, ChatChannel> = {
    '/s': 'say',
    '/say': 'say',
    '/y': 'yell',
    '/yell': 'yell',
    '/w': 'whisper',
    '/whisper': 'whisper',
    '/p': 'party',
    '/party': 'party',
    '/g': 'guild',
    '/guild': 'guild',
    '/t': 'trade',
    '/trade': 'trade',
    '/1': 'world',
    '/world': 'world',
  };

  // Callbacks
  public onSendMessage?: (channel: ChatChannel, message: string, targetId?: string) => void;
  public onWhisper?: (targetName: string, message: string) => void;

  constructor() {
    // Default tabs
    this.tabs = [
      { id: 'general', name: 'General', channels: ['say', 'yell', 'system'], unread: 0 },
      { id: 'combat', name: 'Combat', channels: ['system'], unread: 0 },
      { id: 'whispers', name: 'Whispers', channels: ['whisper'], unread: 0 },
      { id: 'party', name: 'Party', channels: ['party'], unread: 0 },
      { id: 'guild', name: 'Guild', channels: ['guild'], unread: 0 },
      { id: 'trade', name: 'Trade', channels: ['trade', 'world'], unread: 0 },
    ];

    this.container = document.createElement('div');
    this.container.id = 'chat-ui';
    this.container.style.cssText = `
      position: fixed;
      bottom: 10px;
      left: 10px;
      width: 400px;
      height: 250px;
      background: rgba(15, 20, 30, 0.85);
      border: 1px solid #4a4a6a;
      border-radius: 8px;
      z-index: 800;
      font-family: 'Segoe UI', Arial, sans-serif;
      display: flex;
      flex-direction: column;
      transition: height 0.2s;
    `;

    document.body.appendChild(this.container);
    this.render();
    this.setupGlobalListeners();

    // Add welcome message
    this.addSystemMessage('Welcome to Laika! Press ENTER to chat.');
  }

  private setupGlobalListeners(): void {
    // Enter to focus chat
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !this.isInputFocused && !this.isOtherInputFocused()) {
        e.preventDefault();
        this.focusInput();
      }
      // ESC to unfocus
      if (e.key === 'Escape' && this.isInputFocused) {
        this.blurInput();
      }
    });
  }

  private isOtherInputFocused(): boolean {
    const active = document.activeElement;
    if (!active) return false;
    if (active === this.inputElement) return false;
    return active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement;
  }

  private render(): void {
    this.container.innerHTML = `
      <!-- Header with tabs -->
      <div style="display: flex; border-bottom: 1px solid #4a4a6a; min-height: 28px;">
        <div style="flex: 1; display: flex; overflow-x: auto; scrollbar-width: none;">
          ${this.tabs.map(tab => this.renderTab(tab)).join('')}
        </div>
        <button id="chat-minimize" style="
          background: transparent;
          border: none;
          color: #888;
          padding: 0 10px;
          cursor: pointer;
          font-size: 14px;
        ">${this.isMinimized ? '▲' : '▼'}</button>
      </div>

      <!-- Messages -->
      <div id="chat-messages" style="
        flex: 1;
        overflow-y: auto;
        padding: 8px;
        font-size: 13px;
        line-height: 1.4;
        display: ${this.isMinimized ? 'none' : 'block'};
      ">
        ${this.renderMessages()}
      </div>

      <!-- Input area -->
      <div style="
        display: ${this.isMinimized ? 'none' : 'flex'};
        border-top: 1px solid #4a4a6a;
        padding: 6px;
        gap: 6px;
      ">
        <select id="chat-channel" style="
          background: #1a1a2e;
          border: 1px solid #4a4a6a;
          color: ${this.channelColors[this.currentChannel]};
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 11px;
          cursor: pointer;
        ">
          <option value="say" style="color: ${this.channelColors.say}">Say</option>
          <option value="yell" style="color: ${this.channelColors.yell}">Yell</option>
          <option value="party" style="color: ${this.channelColors.party}">Party</option>
          <option value="guild" style="color: ${this.channelColors.guild}">Guild</option>
          <option value="trade" style="color: ${this.channelColors.trade}">Trade</option>
          <option value="world" style="color: ${this.channelColors.world}">World</option>
        </select>
        <input id="chat-input" type="text" placeholder="Press Enter to chat..." maxlength="200" style="
          flex: 1;
          background: #0d1520;
          border: 1px solid #4a4a6a;
          border-radius: 4px;
          padding: 6px 10px;
          color: #e0e0e0;
          font-size: 13px;
          outline: none;
        ">
        <button id="chat-send" style="
          background: #4a90e2;
          border: none;
          color: white;
          padding: 6px 12px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 12px;
        ">Send</button>
      </div>
    `;

    this.messagesContainer = document.getElementById('chat-messages');
    this.inputElement = document.getElementById('chat-input') as HTMLInputElement;

    this.attachEventListeners();
    this.scrollToBottom();
  }

  private renderTab(tab: ChatTab): string {
    const isActive = tab.id === this.activeTabId;
    return `
      <button class="chat-tab" data-tab="${tab.id}" style="
        background: ${isActive ? 'rgba(74, 144, 226, 0.2)' : 'transparent'};
        border: none;
        border-bottom: ${isActive ? '2px solid #4a90e2' : '2px solid transparent'};
        color: ${isActive ? '#4a90e2' : '#888'};
        padding: 6px 12px;
        cursor: pointer;
        font-size: 11px;
        white-space: nowrap;
        position: relative;
      ">
        ${tab.name}
        ${tab.unread > 0 ? `<span style="
          position: absolute;
          top: 2px;
          right: 2px;
          background: #e74c3c;
          color: white;
          font-size: 9px;
          padding: 1px 4px;
          border-radius: 8px;
        ">${tab.unread}</span>` : ''}
      </button>
    `;
  }

  private renderMessages(): string {
    const activeTab = this.tabs.find(t => t.id === this.activeTabId);
    if (!activeTab) return '';

    const filteredMessages = this.messages.filter(m =>
      activeTab.channels.includes(m.channel)
    );

    if (filteredMessages.length === 0) {
      return '<div style="color: #666; text-align: center; padding: 20px;">No messages yet</div>';
    }

    return filteredMessages.map(msg => this.renderMessage(msg)).join('');
  }

  private renderMessage(msg: ChatMessage): string {
    const color = this.channelColors[msg.channel];
    const time = new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    let prefix = '';
    let senderDisplay = msg.senderName;

    switch (msg.channel) {
      case 'say':
        prefix = '';
        break;
      case 'yell':
        prefix = '';
        senderDisplay = msg.senderName.toUpperCase();
        break;
      case 'whisper':
        if (msg.targetName) {
          prefix = `To [${msg.targetName}]: `;
        } else {
          prefix = `From [${msg.senderName}]: `;
          senderDisplay = '';
        }
        break;
      case 'party':
        prefix = '[Party] ';
        break;
      case 'guild':
        prefix = '[Guild] ';
        break;
      case 'trade':
        prefix = '[Trade] ';
        break;
      case 'world':
        prefix = '[World] ';
        break;
      case 'system':
        return `<div style="color: #ffff00; padding: 2px 0; font-style: italic;">${msg.content}</div>`;
    }

    return `
      <div style="padding: 2px 0; word-wrap: break-word;">
        <span style="color: #666; font-size: 10px;">[${time}]</span>
        <span style="color: ${color};">${prefix}</span>
        ${senderDisplay ? `
          <span class="chat-player-name" data-player="${msg.senderId}" style="color: ${color}; cursor: pointer;" title="Click to whisper">
            [${senderDisplay}]
          </span>
          <span style="color: ${color};">: </span>
        ` : ''}
        <span style="color: ${color};">${this.escapeHtml(msg.content)}</span>
      </div>
    `;
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  private attachEventListeners(): void {
    // Tab clicks
    document.querySelectorAll('.chat-tab').forEach(tab => {
      tab.addEventListener('click', (e) => {
        const tabId = (e.currentTarget as HTMLElement).dataset.tab;
        if (tabId) {
          this.activeTabId = tabId;
          const activeTab = this.tabs.find(t => t.id === tabId);
          if (activeTab) activeTab.unread = 0;
          this.render();
        }
      });
    });

    // Minimize button
    document.getElementById('chat-minimize')?.addEventListener('click', () => {
      this.isMinimized = !this.isMinimized;
      this.container.style.height = this.isMinimized ? '28px' : '250px';
      this.render();
    });

    // Channel selector
    const channelSelect = document.getElementById('chat-channel') as HTMLSelectElement;
    if (channelSelect) {
      channelSelect.value = this.currentChannel;
      channelSelect.addEventListener('change', () => {
        this.currentChannel = channelSelect.value as ChatChannel;
        channelSelect.style.color = this.channelColors[this.currentChannel];
      });
    }

    // Input focus/blur
    if (this.inputElement) {
      this.inputElement.addEventListener('focus', () => {
        this.isInputFocused = true;
      });

      this.inputElement.addEventListener('blur', () => {
        this.isInputFocused = false;
      });

      // Prevent game input when typing
      this.inputElement.addEventListener('keydown', (e) => {
        e.stopPropagation();

        if (e.key === 'Enter') {
          this.sendCurrentMessage();
        } else if (e.key === 'Tab') {
          e.preventDefault();
          this.cycleChannel();
        } else if (e.key === 'Escape') {
          this.blurInput();
        }
      });
    }

    // Send button
    document.getElementById('chat-send')?.addEventListener('click', () => {
      this.sendCurrentMessage();
    });

    // Player name clicks for whisper
    document.querySelectorAll('.chat-player-name').forEach(name => {
      name.addEventListener('click', (e) => {
        const playerId = (e.currentTarget as HTMLElement).dataset.player;
        const playerName = (e.currentTarget as HTMLElement).textContent?.replace(/[\[\]]/g, '');
        if (playerName && this.inputElement) {
          this.currentChannel = 'whisper';
          this.whisperTarget = playerName;
          this.inputElement.value = `/w ${playerName} `;
          this.inputElement.focus();
          const select = document.getElementById('chat-channel') as HTMLSelectElement;
          if (select) {
            select.value = 'whisper';
            select.style.color = this.channelColors.whisper;
          }
        }
      });
    });
  }

  private sendCurrentMessage(): void {
    if (!this.inputElement) return;

    let message = this.inputElement.value.trim();
    if (!message) {
      this.blurInput();
      return;
    }

    // Parse channel prefix
    let channel = this.currentChannel;
    let targetName: string | undefined;

    // Check for /command prefix
    const parts = message.split(' ');
    const firstWord = parts[0].toLowerCase();

    if (this.channelPrefixes[firstWord]) {
      channel = this.channelPrefixes[firstWord];
      message = parts.slice(1).join(' ');

      // Handle whisper with target
      if (channel === 'whisper' && parts.length >= 2) {
        targetName = parts[1];
        message = parts.slice(2).join(' ');
      }
    }

    if (!message) {
      this.inputElement.value = '';
      return;
    }

    // Send message
    if (channel === 'whisper' && targetName) {
      this.onWhisper?.(targetName, message);
    } else {
      this.onSendMessage?.(channel, message);
    }

    this.inputElement.value = '';
    this.blurInput();
  }

  private cycleChannel(): void {
    const channels: ChatChannel[] = ['say', 'party', 'guild', 'trade', 'world'];
    const currentIndex = channels.indexOf(this.currentChannel);
    this.currentChannel = channels[(currentIndex + 1) % channels.length];

    const select = document.getElementById('chat-channel') as HTMLSelectElement;
    if (select) {
      select.value = this.currentChannel;
      select.style.color = this.channelColors[this.currentChannel];
    }
  }

  // Public API - Legacy compatibility
  public addMessage(playerName: string, message: string): void {
    this.messages.push({
      id: `msg_${Date.now()}_${Math.random()}`,
      channel: 'say',
      senderId: playerName,
      senderName: playerName,
      content: message,
      timestamp: Date.now(),
    });

    // Limit message history
    if (this.messages.length > this.maxMessages) {
      this.messages = this.messages.slice(-this.maxMessages);
    }

    this.render();
    this.scrollToBottom();
  }

  public addChatMessage(message: ChatMessage): void {
    this.messages.push(message);

    // Limit message history
    if (this.messages.length > this.maxMessages) {
      this.messages = this.messages.slice(-this.maxMessages);
    }

    // Update unread counts for inactive tabs
    for (const tab of this.tabs) {
      if (tab.id !== this.activeTabId && tab.channels.includes(message.channel)) {
        tab.unread++;
      }
    }

    this.render();
    this.scrollToBottom();
  }

  public addSystemMessage(content: string): void {
    this.addChatMessage({
      id: `sys_${Date.now()}`,
      channel: 'system',
      senderId: 'system',
      senderName: 'System',
      content,
      timestamp: Date.now(),
    });
  }

  private scrollToBottom(): void {
    if (this.messagesContainer) {
      this.messagesContainer.scrollTop = this.messagesContainer.scrollHeight;
    }
  }

  public focusInput(): void {
    if (this.isMinimized) {
      this.isMinimized = false;
      this.container.style.height = '250px';
      this.render();
    }
    setTimeout(() => {
      this.inputElement?.focus();
    }, 0);
  }

  public blurInput(): void {
    this.inputElement?.blur();
  }

  public isChatOpen(): boolean {
    return this.isInputFocused;
  }

  public isTyping(): boolean {
    return this.isInputFocused;
  }

  public open(): void {
    this.focusInput();
  }

  public close(): void {
    this.blurInput();
  }

  public setChannel(channel: ChatChannel): void {
    this.currentChannel = channel;
    const select = document.getElementById('chat-channel') as HTMLSelectElement;
    if (select) {
      select.value = channel;
      select.style.color = this.channelColors[channel];
    }
  }

  public clearMessages(): void {
    this.messages = [];
    this.render();
  }

  public destroy(): void {
    this.container.remove();
  }

  public dispose(): void {
    this.container.remove();
  }
}
